import { useDraggable } from '@dnd-kit/core';
import classNames from 'classnames';
import { FC, ReactElement, useMemo } from 'react';
import { Icons } from 'src/assets/fonts/Icons/icons';
import theme from 'src/constants/styles';
import { localeStore } from 'src/mobx/localesStore';
import { TTranslate } from '~/types/main';
import { getTranslate } from 'src/utils/helpers';
import { IConfirmationMessageProps, confirmDelete } from 'src/utils/modalConfirmations';
import Icon from 'src/view/components/Controls/Icon/Icon';
import './style.scss';

type TPropsBranch = {
  id: number;
  name: TTranslate;
  franchise_profile?: string;
};

interface IProps {
  branch: TPropsBranch;
  onDeleteBranch?: (id: TPropsBranch['id']) => void;
  lockDelete?: boolean;
  deleteConfirmationOptions?: IConfirmationMessageProps;
  availableDragAndDrop?: boolean;
  isOverlayBranch?: boolean;
  className?: string;
  notEditable?: boolean;
}

export const ProfileBranch: FC<IProps> = ({ branch, availableDragAndDrop = false, ...props }: IProps): ReactElement => {
  const availableDelete = useMemo((): boolean => Boolean(!props.notEditable && !props.lockDelete), [props.notEditable]);
  const { attributes, isDragging, listeners, setNodeRef } = useDraggable({ id: props.isOverlayBranch ? '0' : String(branch.id) });

  const deleteBranch = () => {
    if (!availableDelete) return;

    confirmDelete({
      ...{ message: localeStore.t('confirm_removing_branch'), onConfirm: () => props.onDeleteBranch!(branch.id) },
      ...props.deleteConfirmationOptions,
    });
  };

  const className = useMemo((): string => {
    return classNames('profile-branch ml-2', props.className, { 'branches-list-item__drag-clone': isDragging });
  }, [isDragging, props.className]);

  return (
    <div ref={props.isOverlayBranch ? undefined : setNodeRef} className={className}>
      <div className="d-flex flex-row gap-10 flex-wrap">
        {availableDragAndDrop ? (
          // eslint-disable-next-line react/jsx-props-no-spreading
          <button className="profile-branch__icon-wrapper py-1 pr-1" {...listeners} {...attributes}>
            <Icon name={Icons.DragHand} size={14} color={theme.colors.gray2} className="profile-branch__icon" />
          </button>
        ) : null}
        <p className="profile-branch__name text-elipsis">{getTranslate(branch.name)}</p>
        {branch.franchise_profile && <p className="family-medium color-gray2 text-size-18">{branch.franchise_profile}</p>}
      </div>

      {availableDelete ? (
        <Icon name={Icons.Close} size={16} color={theme.colors.gray2} onPress={deleteBranch} className="p-1 cursor-pointer" />
      ) : null}
    </div>
  );
};
